import * as React from 'react';
import { Button, Checkbox, Dropdown, MessageBar, MessageBarBody, Option, Spinner } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { getProgram, STAGES } from '../../api/src/programs';
import { useApp } from '../app/context';
import { useHashParts } from '../app/router';
import { applicationView, type IApplicationView, itemView, localToday, stageLabel } from '../records/model';
import { eventView } from '../records/events';
import { needsYou } from '../records/today';
import { useLoad } from '../records/useLoad';
import { NotSetUp } from './Clients';
import NewApplication from './NewApplication';
import ApplicationPage from './ApplicationPage';

/** Applications: the board by stage, opening a new one, or one application's page. */
export default function Applications(): React.ReactElement {
  const { parts, query } = useHashParts();
  const app = useApp();
  if (!app.workspace.firm?.listsReady) {
    return <NotSetUp />;
  }
  if (parts[0] === 'new') {
    return <NewApplication clientKey={query.get('client') ?? undefined} />;
  }
  if (parts[0]) {
    return <ApplicationPage applicationKey={parts[0]} tab={parts[1]} show={query.get('show') ?? undefined} />;
  }
  return <Board />;
}

function Board(): React.ReactElement {
  const app = useApp();
  const { data, error } = useLoad(async () => {
    const [apps, clients, events, uploaded] = await Promise.all([
      app.records.fromSets(set => set.applications.query({ Open: true })), app.records.allClients(), app.records.fromSets(set => set.events.all()),
      app.records.fromSets(set => set.checklist.query({ Status: 'Uploaded by client (check)' })).catch(() => [])
    ]);
    const names: { [key: string]: string } = {};
    for (const c of clients) {
      names[c.key] = c.name;
    }
    const views: IApplicationView[] = apps.map(applicationView);
    // The first thing each file needs, from the same list as Today.
    const needs: { [key: string]: string } = {};
    for (const n of needsYou({ apps: views, events: events.map(eventView), clients, uploaded: uploaded.map(itemView), today: localToday(), names })) {
      for (const a of views.filter(x => x.key === n.applicationKey || x.clientKey === n.clientKey)) {
        needs[a.key] = needs[a.key] ?? n.title;
      }
    }
    return { apps: views, names, needs };
  }, [app.records]);
  const staff = app.workspace.firm?.staff ?? [];
  const [person, setPerson] = React.useState<string>('');
  const [program, setProgram] = React.useState<string>('');
  const [onlyNeeds, setOnlyNeeds] = React.useState<boolean>(false);
  const [view, setView] = React.useState<'board' | 'list'>('board');
  const shown: IApplicationView[] = (data?.apps ?? [])
    .filter(a => (!person || a.lawyer === person || a.clerk === person) && (!program || a.programId === program) && (!onlyNeeds || !!data?.needs[a.key]))
    .sort((a, b) => (a.nextDue ?? '9').localeCompare(b.nextDue ?? '9'));
  const programs: string[] = (data?.apps ?? []).map(a => a.programId).filter((p, i, all) => all.indexOf(p) === i);
  const attention: number = Object.keys(data?.needs ?? {}).length;
  const line = (a: IApplicationView): string => [
    a.restricted ? 'Restricted' : getProgram(a.programId)?.short ?? a.programId,
    a.stage === 'collecting' && a.outstanding ? `${a.outstanding} to collect` : ''
  ].filter(Boolean).join(' · ');

  return (
    <main className="page" style={{ maxWidth: 'none' }}>
      <div className="page-head">
        <div className="stack">
          <h1>Applications</h1>
          {data && <span className="muted" style={{ fontSize: 15 }}>{data.apps.length} open{attention ? <> · <span className="needs-you">{attention} need attention</span></> : null}</span>}
        </div>
        <Button appearance="primary" icon={<AddRegular />} as="a" href="#/applications/new">Open an application</Button>
      </div>
      {data && data.apps.length > 0 && (
        <div className="filters">
          <Dropdown aria-label="Whose files" value={person || 'Everyone\'s files'} selectedOptions={[person]} onOptionSelect={(_, d) => setPerson(d.optionValue ?? '')} style={{ minWidth: 170 }}>
            <Option value="">Everyone's files</Option>
            {staff.map(s => <Option key={s.name} value={s.name}>{s.name}</Option>)}
          </Dropdown>
          <Dropdown aria-label="Program" value={program ? getProgram(program)?.short ?? program : 'All programs'} selectedOptions={[program]} onOptionSelect={(_, d) => setProgram(d.optionValue ?? '')} style={{ minWidth: 170 }}>
            <Option value="">All programs</Option>
            {programs.map(p => <Option key={p} value={p}>{getProgram(p)?.short ?? p}</Option>)}
          </Dropdown>
          <Checkbox checked={onlyNeeds} onChange={(_, d) => setOnlyNeeds(!!d.checked)} label="Only files that need attention" />
          <div className="seg" role="group" aria-label="View" style={{ marginLeft: 'auto' }}>
            <button type="button" aria-pressed={view === 'board'} onClick={() => setView('board')}>Board</button>
            <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>List</button>
          </div>
        </div>
      )}
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {!data && !error && <Spinner size="small" label="Loading applications…" labelPosition="after" />}
      {data && data.apps.length === 0 && <div className="card"><span className="small" style={{ fontSize: 15 }}>No open applications. Open one from a client's page, or with the button above.</span></div>}
      {data && data.apps.length > 0 && view === 'board' && (
        <div className="board">
          {STAGES.filter(s => s.id !== 'closed').map(s => {
            const col: IApplicationView[] = shown.filter(a => a.stage === s.id);
            return (
              <div key={s.id} className="board-col">
                <h3><span>{s.label}</span><span className="muted">{col.length}</span></h3>
                {col.map(a => (
                  <a key={a.key} className="board-card" href={`#/applications/${encodeURIComponent(a.key)}`}>
                    <strong style={{ fontSize: 15 }}>{data.names[a.clientKey] ?? a.title}</strong>
                    <span className="small">{line(a)}</span>
                    {data.needs[a.key] && <span className="attn">{data.needs[a.key]}</span>}
                  </a>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {data && data.apps.length > 0 && view === 'list' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr><th>Client</th><th>Program</th><th>Stage</th><th>Next date</th><th>Needs</th></tr></thead>
            <tbody>{shown.map(a => (
              <tr key={a.key}>
                <td><a href={`#/applications/${encodeURIComponent(a.key)}`}>{data.names[a.clientKey] ?? a.title}</a>{a.restricted && <span className="tag-warn">Restricted</span>}</td>
                <td className="muted">{getProgram(a.programId)?.short ?? a.programId}</td>
                <td className="muted">{stageLabel(a.stage)}</td>
                <td className="muted">{a.nextDue ?? ''}</td>
                <td>{data.needs[a.key] ? <span className="needs-you">{data.needs[a.key]}</span> : null}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </main>
  );
}
